import { Box, FormLabel, Grid2 as Grid, Typography } from '@mui/material';
import { Checkbox, DateInput, EnumDropdown, TextInput, useFormContext, WysiwygInput } from 'framework/form';
import { FormInputGroup } from 'framework/ui/layout';
import { SanitizedHtml } from 'framework/utils';
import { ACCESS_INFO_ACTIONS, ACCESS_INFO_SECTIONS, useProjectSecurityContext } from 'domain/shared/newProject';
import { ASSUMPTION_STATUS_OPTIONS } from './constants';
import { ASSUMPTION_STATUS, AssumptionFormValues } from '../types';
import { PROJECT_MEMBERSHIP_ROLES } from 'domain/shared/core/constants';

const ROLE_OPTIONS = {
  PROJECT_MANAGER: PROJECT_MEMBERSHIP_ROLES.PROJECT_MANAGER,
  QA_MANAGER: PROJECT_MEMBERSHIP_ROLES.QA_MANAGER,
  TEAM_LEAD: PROJECT_MEMBERSHIP_ROLES.TEAM_LEAD,
  BUSINESS_ANALYST: PROJECT_MEMBERSHIP_ROLES.BUSINESS_ANALYST,
};

const { [ASSUMPTION_STATUS.INACTIVE]: _inactive, ...STATUS_OPTIONS_NO_INACTIVE } = ASSUMPTION_STATUS_OPTIONS;

const AssumptionForm = () => {
  const { data } = useFormContext();
  const { isAccessGranted } = useProjectSecurityContext();
  const isManage = isAccessGranted(ACCESS_INFO_SECTIONS.ASSUMPTION, ACCESS_INFO_ACTIONS.MANAGE);
  const isReview = isAccessGranted(ACCESS_INFO_SECTIONS.ASSUMPTION, ACCESS_INFO_ACTIONS.REVIEW);
  const isFull = isManage && !isReview;
  const statusOptions = isFull ? ASSUMPTION_STATUS_OPTIONS : STATUS_OPTIONS_NO_INACTIVE;

  const isExceptionCheckboxVisible = (formData: AssumptionFormValues) =>
    isFull && formData?.status === ASSUMPTION_STATUS.VIOLATED;

  const isExceptionInputsVisible = (formData: AssumptionFormValues) =>
    isExceptionCheckboxVisible(formData) && Boolean(formData?.requestException);

  return (
    <Box
      sx={{
        '& .MuiInputBase-root': { backgroundColor: 'background.default' },
        '& .ck.ck-content.ck-editor__editable': { backgroundColor: 'background.default' },
        '& .ck.ck-toolbar': { backgroundColor: 'background.default' },
        '& .floating-label-date-input-layout input': { backgroundColor: 'background.default' },
      }}
    >
      <Grid container spacing={2}>
        <Grid size={12}>
          {!isManage && !isReview ? (
            <Box display="flex" flexDirection="column">
              <FormLabel>Description</FormLabel>
              <SanitizedHtml html={data?.description ?? ''} />
            </Box>
          ) : (
            <WysiwygInput
              source="description"
              label={
                <Box display="flex" flexDirection="column">
                  Description
                  <Typography variant="caption" color="textSecondary" mt={1}>
                    Provide a clear and concise description of the assumption, outlining the specific conditions or
                    factors that are being assumed to be true for the project. This should include any relevant context
                    or background information that helps to clarify the nature of the assumption and its potential
                    impact on the project.
                  </Typography>
                </Box>
              }
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <EnumDropdown
            source="assignedRole"
            label="Responsible role"
            multiple={false}
            options={ROLE_OPTIONS}
            placeholder="Select"
            disabled={!isManage && !isReview}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <EnumDropdown source="status" label="Status" multiple={false} options={statusOptions} placeholder="Select" />
        </Grid>
        <FormInputGroup isVisible={isExceptionCheckboxVisible}>
          <Grid size={12}>
            <Checkbox source="requestException" label="Request exception for assumption violation" />
          </Grid>
        </FormInputGroup>
        <FormInputGroup isVisible={isExceptionInputsVisible}>
          <Grid size={12}>
            <TextInput source="exceptionReason" label="Exception reason" />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DateInput source="exceptionUntilDate" label="Requested exception until" />
          </Grid>
        </FormInputGroup>
      </Grid>
    </Box>
  );
};

export default AssumptionForm;
